import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Heart, BookOpen, Users, Sparkles, ExternalLink } from 'lucide-react';

/**
 * Credits - Acknowledgments and inspirations for ElchmE
 * Supports both internal state (with trigger) and external control (open/onOpenChange props)
 */
const Credits = ({ triggerClassName = '', open: externalOpen, onOpenChange: externalOnOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use external control if provided, otherwise use internal state
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? externalOnOpenChange : setInternalOpen;

  const creditSections = [
    {
      title: 'Foundation',
      icon: BookOpen,
      color: 'amber',
      credits: [
        {
          name: 'Nonviolent Communication (NVC)',
          role: 'The foundational framework',
          description: 'The principles of Nonviolent Communication by Marshall Rosenberg form the heart of ElchmE\'s OFNR process.'
        }
      ]
    },
    {
      title: 'Visionaries & Teachers',
      icon: Sparkles,
      color: 'blue',
      credits: [
        {
          name: 'Steve Torma',
          role: 'NVC Teacher & Guide',
          description: 'For bringing NVC alive and showing how compassion transforms conflict.'
        },
        {
          name: 'Carrie Branovan',
          role: 'Community Builder',
          description: 'For fostering belonging and connection in countless lives.'
        },
        {
          name: 'Grant Hicks',
          role: 'Mathematical Consciousness',
          description: 'The Grant Math formula that powers our sacred geometry visualizations.'
        },
        {
          name: 'Dr. Anthony Macasaet',
          role: 'Wisdom Keeper',
          description: 'For insights into healing, family systems, and the alchemy of love.'
        },
        {
          name: 'Iona',
          role: 'Creative Spirit',
          description: 'For artistic vision and the courage to imagine new possibilities.'
        }
      ]
    },
    {
      title: 'The Community',
      icon: Users,
      color: 'emerald',
      credits: [
        {
          name: 'The Belonging Sessions Family',
          role: 'Our Community',
          description: 'Every participant who has shared their stories, struggles, and breakthroughs.'
        },
        {
          name: 'Defiance Springs',
          role: 'Home Base',
          description: 'The spiritual home where these ideas took root and flourished.'
        },
        {
          name: 'All the Seedy Peeps',
          role: 'The Pioneers',
          description: 'Those brave souls who planted seeds of change and watched them grow.'
        }
      ]
    },
    {
      title: 'Inspirations',
      icon: Heart,
      color: 'rose',
      credits: [
        {
          name: 'Marshall Rosenberg',
          role: 'Creator of NVC',
          description: 'Whose life work made this all possible.'
        },
        {
          name: 'The Golden Ratio',
          role: 'Sacred Mathematics',
          description: 'The universal constant (φ = 1.618...) that underlies our visual alchemy.'
        },
        {
          name: 'You',
          role: 'The Alchemist',
          description: 'For having the courage to transform conflict into gold.'
        }
      ]
    }
  ];

  const colorMap = {
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: 'text-amber-400' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: 'text-blue-400' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: 'text-emerald-400' },
    rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', icon: 'text-rose-400' },
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Only show trigger when not externally controlled */}
      {!isControlled && (
        <DialogTrigger asChild>
          <button 
            className={`hover:text-amber-400 transition-colors ${triggerClassName}`}
            data-testid="credits-trigger"
          >
            Credits & Gratitude
          </button>
        </DialogTrigger>
      )}
      <DialogContent 
        className="bg-[#0f1419] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="credits-dialog"
      >
        <DialogHeader>
          <DialogTitle 
            className="text-2xl text-center text-white flex items-center justify-center gap-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <Heart className="w-6 h-6 text-amber-400" />
            Credits & Gratitude
          </DialogTitle>
          <p className="text-center text-white/50 text-sm mt-2">
            ElchmE stands on the shoulders of giants
          </p>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {creditSections.map((section, idx) => {
            const colors = colorMap[section.color];
            const IconComponent = section.icon;
            
            return (
              <div key={idx} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                    <IconComponent className={`w-4 h-4 ${colors.icon}`} />
                  </div>
                  <h3 className={`font-medium ${colors.text}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                    {section.title}
                  </h3>
                </div>
                
                <div className="space-y-2 pl-10">
                  {section.credits.map((credit, creditIdx) => (
                    <div 
                      key={creditIdx}
                      className="bg-white/5 rounded-lg p-3 border border-white/5 hover:border-white/10 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-medium text-sm">{credit.name}</h4>
                          <p className="text-white/40 text-xs">{credit.role}</p>
                        </div>
                      </div>
                      <p className="text-white/60 text-xs mt-2 leading-relaxed">
                        {credit.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Footer quote */}
          <div className="pt-4 border-t border-white/10 text-center">
            <blockquote 
              className="text-white/50 text-sm italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              "The craziest thing of all is that love and compassion alchemize conflict and pain into Gold."
            </blockquote>
            <p className="text-white/30 text-xs mt-2">— The Way of ElchmE</p>
          </div>

          {/* Links */}
          <div className="flex justify-center gap-4 text-xs text-white/40 pt-2">
            <a 
              href="https://www.cnvc.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-amber-400 transition-colors flex items-center gap-1"
            >
              Learn NVC <ExternalLink className="w-3 h-3" />
            </a>
            <span>•</span>
            <span>Part of The Belonging Sessions</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Credits;
